import React, { useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const PriceConfigAddModal = ({ onAddSuccess }) => {
  const [currencyCode, setCurrencyCode] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [profitPercent, setProfitPercent] = useState("");
  const [serviceFeePercent, setServiceFeePercent] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanCurrency = currencyCode.trim().toUpperCase();
    const cleanCountry = countryCode.trim().toUpperCase();
    const profitVal = parseFloat(profitPercent);
    const serviceFeeVal = parseFloat(serviceFeePercent);

    // 1. Validations
    if (!cleanCurrency) {
      setErrorMsg("❌ Currency Code is required.");
      return;
    }
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) {
      setErrorMsg("❌ Currency Code must be exactly 3 letters (e.g., AUD, USD).");
      return;
    }

    // if (!cleanCountry) {
    //   setErrorMsg("❌ Country Code is required.");
    //   return;
    // }
    // if (!/^[A-Z]{2}$/.test(cleanCountry)) {
    //   setErrorMsg("❌ Country Code must be exactly 2 letters (e.g., AU, US).");
    //   return;
    // }

    if (isNaN(profitVal) || profitVal < 0) {
      setErrorMsg("❌ Profit % must be a number greater than or equal to 0.");
      return;
    }

    if (isNaN(serviceFeeVal) || serviceFeeVal < 0) {
      setErrorMsg("❌ Service Fee must be a number greater than or equal to 0.");
      return;
    }

    setErrorMsg("");
    setSuccessMsg("");
    setLoading(true);

    try {
      // 2. Duplicate Check using currencyCode as Document ID
      const docRef = doc(db, "price_config", cleanCurrency);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setErrorMsg(`❌ Price configuration for currency "${cleanCurrency}" already exists.`);
        setLoading(false);
        return;
      }

      // 3. Save to Firestore
      await setDoc(docRef, {
        currencyCode: cleanCurrency,
        countryCode: cleanCountry,
        profitPercent: profitVal,
        serviceFeePercent: serviceFeeVal,
        updatedAt: serverTimestamp(),
      });

      setSuccessMsg("✅ Price configuration added successfully!");
      setCurrencyCode("");
      setCountryCode("");
      setProfitPercent("");
      setServiceFeePercent("");

      // Trigger list refresh
      if (onAddSuccess) {
        onAddSuccess();
      }

      // Automatically close modal after a short delay
      setTimeout(() => {
        setSuccessMsg("");
        const closeBtn = document.querySelector("#addPriceConfigModal .btn-close");
        if (closeBtn) {
          closeBtn.click();
        }
      }, 1500);

    } catch (error) {
      console.error("Error adding price config:", error);
      setErrorMsg("❌ Failed to add price configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="addPriceConfigModal"
      tabIndex="-1"
      aria-labelledby="addPriceConfigModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow border-0" style={{ backgroundColor: "#F5F7FA" }}>
          <div className="modal-header border-0 pb-0">
            <h5 className="text-h1 fw-bold text-primary w-100 text-center mt-3" id="addPriceConfigModalLabel">
              Add Price Configuration
            </h5>
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => {
                setErrorMsg("");
                setSuccessMsg("");
              }}
            ></button>
          </div>

          <div className="modal-body px-4 pb-4">
            {errorMsg && (
              <div className="alert alert-danger py-2 rounded-3 small" role="alert">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="alert alert-success py-2 rounded-3 small" role="alert">
                {successMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Currency Code */}
              <div className="mb-3">
                <label htmlFor="currencyCodeInput" className="form-label fw-semibold text-dark">
                  Currency Code
                </label>
                <input
                  id="currencyCodeInput"
                  type="text"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. AUD, USD"
                  value={currencyCode}
                  onChange={(e) => setCurrencyCode(e.target.value)}
                  maxLength={3}
                  required
                  disabled={loading}
                />
              </div>

              {/* Country Code */}
              {/* <div className="mb-3">
                <label htmlFor="countryCodeInput" className="form-label fw-semibold text-dark">
                  Country Code
                </label>
                <input
                  id="countryCodeInput"
                  type="text"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. AU, US"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  maxLength={2}
                  required
                  disabled={loading}
                />
              </div> */}

              {/* Profit Percent */}
              <div className="mb-3">
                <label htmlFor="profitPercentInput" className="form-label fw-semibold text-dark">
                  Profit %
                </label>
                <input
                  id="profitPercentInput"
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. 7.00"
                  value={profitPercent}
                  onChange={(e) => setProfitPercent(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {/* Service Fee */}
              <div className="mb-4">
                <label htmlFor="serviceFeePercentInput" className="form-label fw-semibold text-dark">
                  Fixed Service Fee
                </label>
                <input
                  id="serviceFeePercentInput"
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. 0.50"
                  value={serviceFeePercent}
                  onChange={(e) => setServiceFeePercent(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-primary py-2 rounded-3 fw-semibold shadow-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="admin-loader-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Configuration"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceConfigAddModal;
